import { useState, useEffect, useMemo } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Building2,
  Link2,
  ChevronRight,
  Banknote,
  PiggyBank,
  Loader2,
  Landmark,
  Home,
  Package,
  Watch,
  Gem,
  Coins,
  Bitcoin,
  TrendingUp,
  Shield,
  BarChart3,
  Plus,
  CreditCard,
  Users,
  Percent,
  MapPin,
  Calendar,
  Ruler,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { usePowensConnect } from '@/hooks/usePowensConnect';
import { CustomSelect } from '@/components/ui/custom-select';
import { useAccounts } from '@/hooks/useAccounts';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts';
import { supabase } from '@/lib/supabase';
import {
  BANKS,
  COUNTRY_NAMES,
  findBankByName,
  getBankLogoUrl,
  getCountryFlagUrl,
  getCurrencyForCountry,
} from '@/lib/banks';
import { CURRENCIES, type Currency } from '@/contexts/CurrencyContext';
import { searchWatchModels } from '@/lib/watches';
import { searchPreciousMetals, getPopularMetals, type PreciousMetal } from '@/lib/precious-metals';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  searchCryptos,
  getCryptoPrice,
  POPULAR_CRYPTOS,
  type CryptoSearchResult,
  type CryptoPrice,
} from '@/lib/crypto';
import { searchStocks, getPopularStocks, type Stock } from '@/lib/stocks';
import type { Bank } from '@/lib/banks';

type Step =
  | 'category'
  | 'method'
  | 'bank-select'
  | 'account-form'
  | 'cash-form'
  | 'real-estate-form'
  | 'other-asset-form'
  | 'watches-jewelry-form'
  | 'precious-metals-form'
  | 'crypto-form'
  | 'pea-form';

interface AccountCategory {
  id: string;
  icon: typeof Landmark;
  gradient: string;
  available: boolean;
}

const ACCOUNT_CATEGORIES: AccountCategory[] = [
  {
    id: 'bank_accounts',
    icon: Landmark,
    gradient: 'from-[#16c47f] to-emerald-600',
    available: true,
  },
  { id: 'cash', icon: Banknote, gradient: 'from-green-500 to-green-600', available: true },
  { id: 'real_estate', icon: Home, gradient: 'from-teal-500 to-teal-600', available: true },
  { id: 'other_assets', icon: Package, gradient: 'from-amber-500 to-orange-500', available: true },
  {
    id: 'watches_jewelry',
    icon: Watch,
    gradient: 'from-purple-500 to-violet-600',
    available: true,
  },
  { id: 'precious_metals', icon: Coins, gradient: 'from-yellow-500 to-amber-500', available: true },
  { id: 'crypto', icon: Bitcoin, gradient: 'from-orange-500 to-orange-600', available: true },
  { id: 'pea', icon: BarChart3, gradient: 'from-blue-500 to-blue-600', available: true },
  {
    id: 'life_insurance',
    icon: Shield,
    gradient: 'from-indigo-500 to-indigo-600',
    available: false,
  },
  { id: 'stocks_funds', icon: TrendingUp, gradient: 'from-pink-500 to-rose-500', available: false },
];

const BANK_ACCOUNT_TYPES: { key: string; type: 'asset' | 'liability'; icon: typeof Banknote }[] = [
  { key: 'checking', type: 'asset', icon: Banknote },
  { key: 'savings', type: 'asset', icon: PiggyBank },
  { key: 'credit_card', type: 'liability', icon: CreditCard },
  { key: 'joint_account', type: 'asset', icon: Users },
  { key: 'money_market', type: 'asset', icon: Landmark },
];

export default function AddBankAccountPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { id: editAccountId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { connect: connectPowens, isConnecting: isPowensConnecting } = usePowensConnect();
  const { accounts, createAccount, updateAccount } = useAccounts();
  const { user } = useAuth();
  const { currencyConfig } = useCurrency();
  const editAccount = editAccountId ? accounts.find((a) => a.id === editAccountId) : null;
  const isEditMode = !!editAccountId;

  const editInitialStep = (): Step => {
    if (!editAccount) return 'account-form';
    if (editAccount.subtype === 'cash') return 'cash-form';
    if (editAccount.subtype === 'real_estate') return 'real-estate-form';
    if (editAccount.subtype === 'other_asset') return 'other-asset-form';
    if (editAccount.subtype === 'watch' || editAccount.subtype === 'jewelry')
      return 'watches-jewelry-form';
    if (editAccount.subtype === 'precious_metal') return 'precious-metals-form';
    if (editAccount.subtype === 'crypto') return 'crypto-form';
    if (editAccount.subtype === 'pea_stock') return 'pea-form';
    return 'account-form';
  };
  const [step, setStep] = useState<Step>(isEditMode ? editInitialStep() : 'category');
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userCountry, setUserCountry] = useState<string>('MA');
  const [saving, setSaving] = useState(false);
  const [logoErrors, setLogoErrors] = useState<Set<string>>(new Set());

  // Currency config based on account's country (not global display currency)
  const bankCurrencyCode = getCurrencyForCountry(selectedBank?.country || userCountry) as Currency;
  const [nonBankCurrency, setNonBankCurrency] = useState<Currency>(bankCurrencyCode);
  const isBankStep = step === 'account-form' || step === 'method' || step === 'bank-select';
  const accountCurrencyCode = isBankStep ? bankCurrencyCode : nonBankCurrency;
  const accountCurrencyConfig = CURRENCIES[accountCurrencyCode] || currencyConfig;

  const [formData, setFormData] = useState({
    name: '',
    subtype: 'checking',
    balance: '',
    interestRate: '',
    creditLimit: '',
  });

  // Pre-fill form for edit mode
  useEffect(() => {
    if (editAccount) {
      if (editAccount.subtype === 'cash') {
        setStep('cash-form');
        setCashFormData({
          name: editAccount.name,
          balance: editAccount.balance?.toString() || '',
        });
      } else if (editAccount.subtype === 'real_estate') {
        setStep('real-estate-form');
        const meta = (editAccount.metadata || {}) as Record<string, unknown>;
        setReData({
          name: editAccount.name,
          description: (meta.description as string) || '',
          propertyType: (meta.property_type as string) || 'apartment',
          category: (meta.category as string) || 'primary_residence',
          purchasePrice: meta.purchase_price?.toString() || editAccount.balance?.toString() || '',
          surface: meta.surface?.toString() || '',
          landSurface: meta.land_surface?.toString() || '',
          constructionYear: (meta.construction_year as string) || '',
          dpe: (meta.dpe as string) || '',
          agencyFees: meta.agency_fees?.toString() || '',
          notaryFees: meta.notary_fees?.toString() || '',
          renovationFees: meta.renovation_fees?.toString() || '',
          furnishingFees: meta.furnishing_fees?.toString() || '',
          purchaseDate: (meta.purchase_date as string) || '',
          ownershipPercent: meta.ownership_percent?.toString() || '100',
          city: (meta.city as string) || '',
          country: (meta.country as string) || '',
        });
      } else if (editAccount.subtype === 'other_asset') {
        setStep('other-asset-form');
        const meta2 = (editAccount.metadata || {}) as Record<string, unknown>;
        setOaData({
          name: editAccount.name,
          category: (meta2.asset_category as string) || 'other',
          quantity: meta2.quantity?.toString() || '1',
          purchaseUnitPrice: meta2.purchase_unit_price?.toString() || '',
          currentUnitPrice: meta2.current_unit_price?.toString() || '',
          ownershipPercent: meta2.ownership_percent?.toString() || '100',
        });
      } else if (editAccount.subtype === 'watch' || editAccount.subtype === 'jewelry') {
        setStep('watches-jewelry-form');
        const meta3 = (editAccount.metadata || {}) as Record<string, unknown>;
        setWjData({
          itemType: editAccount.subtype as 'watch' | 'jewelry',
          name: editAccount.name,
          purchasePrice: meta3.purchase_price?.toString() || editAccount.balance?.toString() || '',
          condition: (meta3.condition as string) || '',
          hasOriginalBox: !!meta3.has_original_box,
          hasPapers: !!meta3.has_papers,
          seller: (meta3.seller as string) || '',
          ownershipPercent: meta3.ownership_percent?.toString() || '100',
        });
      } else if (editAccount.subtype === 'precious_metal') {
        setStep('precious-metals-form');
        setPmStep('form');
        const meta4 = (editAccount.metadata || {}) as Record<string, unknown>;
        setPmData({
          name: editAccount.name,
          metalId: (meta4.metal_id as string) || 'custom',
          metalCategory: (meta4.metal_category as string) || 'other',
          quantity: meta4.quantity?.toString() || '1',
          purchaseUnitPrice: meta4.purchase_unit_price?.toString() || '',
          ownershipPercent: meta4.ownership_percent?.toString() || '100',
        });
      } else if (editAccount.subtype === 'crypto') {
        setStep('crypto-form');
        setCryptoStep('form');
        const meta5 = (editAccount.metadata || {}) as Record<string, unknown>;
        setCryptoData({
          name: editAccount.name,
          coinId: (meta5.coin_id as string) || '',
          symbol: (meta5.symbol as string) || '',
          thumb: (meta5.thumb as string) || '',
          quantity: meta5.quantity?.toString() || '1',
          purchaseUnitPrice: meta5.purchase_unit_price?.toString() || '',
        });
      } else if (editAccount.subtype === 'pea_stock') {
        setStep('pea-form');
        setPeaStep('form');
        const meta6 = (editAccount.metadata || {}) as Record<string, unknown>;
        setPeaData({
          name: editAccount.name,
          stockId: (meta6.stock_id as string) || '',
          symbol: (meta6.symbol as string) || '',
          stockType: (meta6.stock_type as string) || 'stock',
          quantity: meta6.quantity?.toString() || '1',
          purchaseUnitPrice: meta6.purchase_unit_price?.toString() || '',
        });
      } else {
        setStep('account-form');
        const bank = findBankByName(editAccount.institution);
        if (bank) setSelectedBank(bank);
        setFormData({
          name: editAccount.name,
          subtype: editAccount.subtype || 'checking',
          balance: editAccount.balance?.toString() || '',
          interestRate: editAccount.interest_rate?.toString() || '',
          creditLimit: editAccount.credit_limit?.toString() || '',
        });
      }
    }
  }, [editAccount]);

  // Fetch user's country from onboarding
  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_onboarding')
      .select('country_code')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.country_code) setUserCountry(data.country_code.toUpperCase());
      });
  }, [user]);

  // Sync nonBankCurrency default with userCountry (only when not editing)
  useEffect(() => {
    if (!isEditMode) {
      setNonBankCurrency(getCurrencyForCountry(userCountry) as Currency);
    }
  }, [userCountry, isEditMode]);

  // Initialize nonBankCurrency from editAccount
  useEffect(() => {
    if (editAccount?.currency) {
      setNonBankCurrency(editAccount.currency as Currency);
    }
  }, [editAccount]);

  // Filter and group banks
  const { userCountryBanks, otherCountryGroups } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = BANKS.filter(
      (b) =>
        !query || b.name.toLowerCase().includes(query) || b.shortName.toLowerCase().includes(query)
    );

    const userBanks = filtered.filter((b) => b.country === userCountry);
    const otherBanks = filtered.filter((b) => b.country !== userCountry && b.country);

    const grouped: Record<string, Bank[]> = {};
    for (const bank of otherBanks) {
      if (!grouped[bank.country]) grouped[bank.country] = [];
      grouped[bank.country].push(bank);
    }

    // Sort country groups alphabetically
    const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
      const nameA = COUNTRY_NAMES[a]?.[language as 'en' | 'fr'] || a;
      const nameB = COUNTRY_NAMES[b]?.[language as 'en' | 'fr'] || b;
      return nameA.localeCompare(nameB);
    });

    return { userCountryBanks: userBanks, otherCountryGroups: sortedGroups };
  }, [searchQuery, userCountry, language]);

  const handleSelectBank = (bank: Bank) => {
    setSelectedBank(bank);
    setFormData((prev) => ({
      ...prev,
      name: bank.shortName || bank.name,
    }));
    setStep('account-form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    if (!isEditMode && !selectedBank) return;

    setSaving(true);
    try {
      const accountType = BANK_ACCOUNT_TYPES.find((t) => t.key === formData.subtype);
      const accountData = {
        name: formData.name,
        type: accountType?.type || 'asset',
        subtype: formData.subtype,
        balance: parseFloat(formData.balance) || 0,
        institution: selectedBank?.name || editAccount?.institution || undefined,
        interest_rate: formData.interestRate ? parseFloat(formData.interestRate) : null,
        credit_limit: formData.creditLimit ? parseFloat(formData.creditLimit) : null,
        currency: getCurrencyForCountry(selectedBank?.country || userCountry),
      };

      if (isEditMode && editAccountId) {
        const { currency: _, ...updateData } = accountData;
        await updateAccount(editAccountId, updateData);
        navigate(`/accounts/${editAccountId}`);
      } else {
        await createAccount(accountData);
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving account:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoError = (bankId: string) => {
    setLogoErrors((prev) => new Set(prev).add(bankId));
  };

  const BankLogo = ({ bank, size = 'md' }: { bank: Bank; size?: 'sm' | 'md' | 'lg' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-14 h-14',
    };
    const iconSizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-7 h-7' };

    if (!bank.domain || logoErrors.has(bank.id)) {
      return (
        <div
          className={`${sizeClasses[size]} rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center`}
        >
          <Landmark className={`${iconSizes[size]} text-gray-400 dark:text-gray-500`} />
        </div>
      );
    }

    return (
      <div
        className={`${sizeClasses[size]} rounded-xl bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 overflow-hidden flex items-center justify-center p-1`}
      >
        <img
          src={getBankLogoUrl(bank)}
          alt={bank.name}
          className="w-full h-full object-contain"
          onError={() => handleLogoError(bank.id)}
        />
      </div>
    );
  };

  // Step 0: Choose account category
  const renderCategoryStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#16c47f] to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#16c47f]/20">
          <Plus className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('addBank.categoryTitle' as any)}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          {t('addBank.categorySubtitle' as any)}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ACCOUNT_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <div key={cat.id} className="relative">
              <button
                disabled={!cat.available}
                onClick={() => {
                  if (!cat.available) return;
                  if (cat.id === 'cash') {
                    setStep('cash-form');
                  } else if (cat.id === 'real_estate') {
                    setStep('real-estate-form');
                  } else if (cat.id === 'other_assets') {
                    setStep('other-asset-form');
                  } else if (cat.id === 'watches_jewelry') {
                    setStep('watches-jewelry-form');
                  } else if (cat.id === 'precious_metals') {
                    setStep('precious-metals-form');
                  } else if (cat.id === 'crypto') {
                    setStep('crypto-form');
                  } else if (cat.id === 'pea') {
                    setStep('pea-form');
                  } else {
                    setStep('method');
                  }
                }}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                  cat.available
                    ? 'border-gray-100 dark:border-gray-700/50 hover:border-[#16c47f] dark:hover:border-[#16c47f] bg-white dark:bg-gray-800 hover:shadow-md cursor-pointer group'
                    : 'border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 opacity-50 cursor-not-allowed'
                }`}
              >
                <div
                  className={`w-11 h-11 rounded-xl bg-gradient-to-br ${cat.gradient} flex items-center justify-center flex-shrink-0 ${cat.available ? 'group-hover:scale-105 transition-transform' : ''}`}
                >
                  <Icon className="w-5.5 h-5.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className={`font-semibold text-sm ${cat.available ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-500'}`}
                  >
                    {t(`addBank.cat.${cat.id}` as any)}
                  </h3>
                  <p
                    className={`text-xs mt-0.5 ${cat.available ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'}`}
                  >
                    {t(`addBank.catDesc.${cat.id}` as any)}
                  </p>
                </div>
                {cat.available && (
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-[#16c47f] transition-colors flex-shrink-0" />
                )}
              </button>
              {!cat.available && (
                <span className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] font-semibold rounded-full uppercase tracking-wide">
                  {t('addBank.comingSoon' as any)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Step 1: Choose method
  const renderMethodStep = () => (
    <div className="max-w-lg mx-auto space-y-4">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#16c47f] to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#16c47f]/20">
          <Building2 className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('addBank.title' as any)}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">{t('addBank.subtitle' as any)}</p>
      </div>

      {/* Open Banking via Powens */}
      <button
        onClick={() => connectPowens()}
        disabled={isPowensConnecting}
        className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-gray-100 dark:border-gray-700/50 hover:border-blue-500 dark:hover:border-blue-500 bg-white dark:bg-gray-800 transition-all duration-200 text-left group disabled:cursor-wait disabled:opacity-60"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          {isPowensConnecting ? (
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          ) : (
            <Link2 className="w-6 h-6 text-white" />
          )}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white">{t('powens.connectBank')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {isPowensConnecting ? t('powens.connecting') : t('powens.connectBankDesc')}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
      </button>

      {/* Manual Addition */}
      <button
        onClick={() => setStep('bank-select')}
        className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-gray-100 dark:border-gray-700/50 hover:border-[#16c47f] dark:hover:border-[#16c47f] bg-white dark:bg-gray-800 transition-all duration-200 text-left group"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#16c47f] to-emerald-600 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('addBank.manualAdd' as any)}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t('addBank.manualAddDesc' as any)}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#16c47f] transition-colors" />
      </button>
    </div>
  );

  // Step 2: Bank selection
  const renderBankSelectStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('addBank.selectBank' as any)}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          {t('addBank.selectBankDesc' as any)}
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('addBank.searchPlaceholder' as any)}
          autoFocus
          className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm"
        />
      </div>

      <div className="space-y-6 max-h-[calc(100vh-350px)] overflow-y-auto pr-1">
        {/* User's country banks */}
        {userCountryBanks.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3 px-1">
              <img
                src={getCountryFlagUrl(userCountry)}
                alt={userCountry}
                className="w-7 h-5 object-cover shadow-sm border border-gray-200 dark:border-gray-600"
              />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {COUNTRY_NAMES[userCountry]?.[language as 'en' | 'fr'] || userCountry}
              </h3>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                — {t('addBank.yourCountry' as any)}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {userCountryBanks.map((bank) => (
                <button
                  key={bank.id}
                  onClick={() => handleSelectBank(bank)}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 hover:border-[#16c47f] dark:hover:border-[#16c47f] hover:shadow-md transition-all duration-200 text-left group"
                >
                  <BankLogo bank={bank} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {bank.name}
                    </p>
                    {bank.shortName !== bank.name && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {bank.shortName}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-[#16c47f] transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Other country groups */}
        {otherCountryGroups.map(([country, banks]) => (
          <div key={country}>
            <div className="flex items-center gap-2 mb-3 px-1">
              <img
                src={getCountryFlagUrl(country)}
                alt={country}
                className="w-7 h-5 object-cover shadow-sm border border-gray-200 dark:border-gray-600"
              />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {COUNTRY_NAMES[country]?.[language as 'en' | 'fr'] || country}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {banks.map((bank) => (
                <button
                  key={bank.id}
                  onClick={() => handleSelectBank(bank)}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 hover:border-[#16c47f] dark:hover:border-[#16c47f] hover:shadow-md transition-all duration-200 text-left group"
                >
                  <BankLogo bank={bank} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {bank.name}
                    </p>
                    {bank.shortName !== bank.name && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {bank.shortName}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-[#16c47f] transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* No results */}
        {userCountryBanks.length === 0 && otherCountryGroups.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('addBank.noResults' as any)}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Step 3: Account form
  const renderAccountForm = () => {
    return (
      <div className="max-w-lg mx-auto">
        {/* Selected bank header */}
        {selectedBank && (
          <div className="flex items-center gap-4 p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 mb-6">
            <BankLogo bank={selectedBank} size="lg" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{selectedBank.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <img
                  src={getCountryFlagUrl(selectedBank.country)}
                  alt={selectedBank.country}
                  className="w-5 h-3.5 object-cover shadow-sm border border-gray-200 dark:border-gray-600 inline-block mr-1"
                />
                {COUNTRY_NAMES[selectedBank.country]?.[language as 'en' | 'fr']}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Account type selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('addBank.accountType' as any)}
            </label>
            <CustomSelect
              value={formData.subtype}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  subtype: value,
                  interestRate: '',
                  creditLimit: '',
                }))
              }
              options={BANK_ACCOUNT_TYPES.map(({ key }) => ({
                value: key,
                label: t(`addBank.bankType.${key}` as any),
              }))}
            />
          </div>

          {/* Interest rate - for savings, money market, and credit card APR */}
          {(formData.subtype === 'savings' ||
            formData.subtype === 'money_market' ||
            formData.subtype === 'credit_card') && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formData.subtype === 'credit_card'
                  ? t('addBank.apr' as any)
                  : t('addBank.interestRate' as any)}
              </label>
              <div className="relative">
                <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.interestRate}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.,]/g, '');
                    setFormData((prev) => ({ ...prev, interestRate: val }));
                  }}
                  placeholder={formData.subtype === 'credit_card' ? '18.90' : '2.50'}
                  className="w-full px-4 pr-11 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm"
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {formData.subtype === 'credit_card'
                  ? t('addBank.aprHint' as any)
                  : t('addBank.interestRateHint' as any)}
              </p>
            </div>
          )}

          {/* Credit limit - for credit card */}
          {formData.subtype === 'credit_card' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('addBank.creditLimit' as any)}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
                  {accountCurrencyConfig.symbol}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.creditLimit}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.,]/g, '');
                    setFormData((prev) => ({ ...prev, creditLimit: val }));
                  }}
                  placeholder="5000"
                  className="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm"
                />
              </div>
            </div>
          )}

          {/* Account name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('accounts.modal.name' as any)}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t('accounts.modal.namePlaceholder' as any)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm"
              required
            />
          </div>

          {/* Balance */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('accounts.modal.balance' as any)}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
                {accountCurrencyConfig.symbol}
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={formData.balance}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.,-]/g, '');
                  setFormData((prev) => ({ ...prev, balance: val }));
                }}
                placeholder="0.00"
                className="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!formData.name || saving}
            className="w-full py-3.5 bg-[#16c47f] hover:bg-[#12b070] disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 mt-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('accounts.modal.saving' as any)}
              </>
            ) : isEditMode ? (
              t('addBank.saveChanges' as any)
            ) : (
              t('addBank.addAccount' as any)
            )}
          </button>
        </form>
      </div>
    );
  };

  // Cash form
  const [cashFormData, setCashFormData] = useState({ name: '', balance: '' });

  const handleCashSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cashFormData.name) return;

    setSaving(true);
    try {
      if (isEditMode && editAccountId) {
        await updateAccount(editAccountId, {
          name: cashFormData.name,
          balance: parseFloat(cashFormData.balance) || 0,
        });
        navigate('/accounts');
      } else {
        await createAccount({
          name: cashFormData.name,
          type: 'asset',
          subtype: 'cash',
          balance: parseFloat(cashFormData.balance) || 0,
          currency: nonBankCurrency,
        });
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving cash account:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderCurrencySelector = () => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {language === 'fr' ? 'Devise' : 'Currency'}
      </label>
      <CustomSelect
        value={nonBankCurrency}
        onChange={(value) => setNonBankCurrency(value as Currency)}
        disabled={isEditMode}
        options={Object.values(CURRENCIES).map((c) => ({
          value: c.code,
          label: `${c.symbol}  ${c.code} — ${language === 'fr' ? c.nameFr : c.name}`,
        }))}
      />
    </div>
  );

  const renderCashForm = () => (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/20">
          <Banknote className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('addCash.title' as any)}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">{t('addCash.subtitle' as any)}</p>
      </div>

      <form onSubmit={handleCashSubmit} className="space-y-5">
        {/* Cash name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('addCash.name' as any)}
          </label>
          <input
            type="text"
            value={cashFormData.name}
            onChange={(e) => setCashFormData((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('addCash.namePlaceholder' as any)}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm"
            autoFocus
            required
          />
          <p className="text-xs text-gray-400 dark:text-gray-500">{t('addCash.nameHint' as any)}</p>
        </div>

        {/* Amount */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('addCash.amount' as any)}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
              {accountCurrencyConfig.symbol}
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={cashFormData.balance}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.,]/g, '');
                setCashFormData((prev) => ({ ...prev, balance: val }));
              }}
              placeholder="0.00"
              className="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm"
            />
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t('addCash.amountHint' as any)}
          </p>
        </div>

        {/* Currency */}
        {renderCurrencySelector()}

        {/* Submit */}
        <button
          type="submit"
          disabled={!cashFormData.name || saving}
          className="w-full py-3.5 bg-[#16c47f] hover:bg-[#12b070] disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 mt-2"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('accounts.modal.saving' as any)}
            </>
          ) : isEditMode ? (
            t('addBank.saveChanges' as any)
          ) : (
            t('addCash.addCash' as any)
          )}
        </button>
      </form>
    </div>
  );

  // Real estate form
  type RealEstateTab = 'description' | 'characteristics' | 'ownership';
  const [reTab, setReTab] = useState<RealEstateTab>('description');
  const [reData, setReData] = useState({
    name: '',
    description: '',
    propertyType: 'apartment',
    category: 'primary_residence',
    purchasePrice: '',
    surface: '',
    landSurface: '',
    constructionYear: '',
    dpe: '',
    agencyFees: '',
    notaryFees: '',
    renovationFees: '',
    furnishingFees: '',
    purchaseDate: '',
    ownershipPercent: '100',
    city: '',
    country: '',
  });

  const handleRealEstateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const purchasePrice = parseFloat(reData.purchasePrice) || 0;
      const ownershipPct = parseFloat(reData.ownershipPercent) || 100;
      const estimatedValue = purchasePrice * (ownershipPct / 100);

      const metadataPayload = {
        description: reData.description || null,
        property_type: reData.propertyType,
        category: reData.category,
        purchase_price: purchasePrice || null,
        surface: parseFloat(reData.surface) || null,
        land_surface: parseFloat(reData.landSurface) || null,
        construction_year: reData.constructionYear || null,
        dpe: reData.dpe || null,
        agency_fees: parseFloat(reData.agencyFees) || null,
        notary_fees: parseFloat(reData.notaryFees) || null,
        renovation_fees: parseFloat(reData.renovationFees) || null,
        furnishing_fees: parseFloat(reData.furnishingFees) || null,
        purchase_date: reData.purchaseDate || null,
        ownership_percent: ownershipPct,
        city: reData.city || null,
        country: reData.country || null,
      };

      if (isEditMode && editAccountId) {
        await updateAccount(editAccountId, {
          name: reData.name || t('addRealEstate.defaultName' as any),
          balance: estimatedValue,
          metadata: metadataPayload,
        });
        navigate(`/accounts/${editAccountId}`);
      } else {
        await createAccount({
          name: reData.name || t('addRealEstate.defaultName' as any),
          type: 'asset',
          subtype: 'real_estate',
          balance: estimatedValue,
          metadata: metadataPayload,
          currency: nonBankCurrency,
        });
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving real estate:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderRealEstateForm = () => {
    const tabs: { key: RealEstateTab; label: string }[] = [
      { key: 'description', label: t('addRealEstate.tab.description' as any) },
      { key: 'characteristics', label: t('addRealEstate.tab.characteristics' as any) },
      { key: 'ownership', label: t('addRealEstate.tab.ownership' as any) },
    ];

    const inputCls =
      'w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all text-sm';
    const lblCls = 'text-sm font-medium text-gray-700 dark:text-gray-300';

    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/20">
            <Home className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('addRealEstate.title' as any)}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            {t('addRealEstate.subtitle' as any)}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setReTab(tab.key)}
              className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                reTab === tab.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleRealEstateSubmit}>
          {/* Tab: Description */}
          {reTab === 'description' && (
            <div className="space-y-5">
              <div className="space-y-2">
                <label className={lblCls}>
                  {t('addRealEstate.name' as any)}{' '}
                  <span className="text-gray-400 text-xs font-normal">
                    {t('addRealEstate.optional' as any)}
                  </span>
                </label>
                <input
                  type="text"
                  value={reData.name}
                  onChange={(e) => setReData((p) => ({ ...p, name: e.target.value }))}
                  placeholder={t('addRealEstate.namePlaceholder' as any)}
                  className={inputCls}
                />
              </div>

              <div className="space-y-2">
                <label className={lblCls}>
                  {t('addRealEstate.description' as any)}{' '}
                  <span className="text-gray-400 text-xs font-normal">
                    {t('addRealEstate.optional' as any)}
                  </span>
                </label>
                <textarea
                  value={reData.description}
                  onChange={(e) => setReData((p) => ({ ...p, description: e.target.value }))}
                  placeholder={t('addRealEstate.descriptionPlaceholder' as any)}
                  rows={3}
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={lblCls}>{t('addRealEstate.propertyType' as any)}</label>
                  <CustomSelect
                    value={reData.propertyType}
                    onChange={(value) => setReData((p) => ({ ...p, propertyType: value }))}
                    options={[
                      'apartment',
                      'house',
                      'building',
                      'parking',
                      'land',
                      'commercial',
                      'other',
                    ].map((v) => ({
                      value: v,
                      label: t(`addRealEstate.type.${v}` as any),
                    }))}
                  />
                </div>

                <div className="space-y-2">
                  <label className={lblCls}>{t('addRealEstate.category' as any)}</label>
                  <CustomSelect
                    value={reData.category}
                    onChange={(value) => setReData((p) => ({ ...p, category: value }))}
                    options={['primary_residence', 'secondary_residence', 'rental', 'other'].map(
                      (v) => ({
                        value: v,
                        label: t(`addRealEstate.cat.${v}` as any),
                      })
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={lblCls}>
                    <MapPin className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />
                    {t('addRealEstate.city' as any)}{' '}
                    <span className="text-gray-400 text-xs font-normal">
                      {t('addRealEstate.optional' as any)}
                    </span>
                  </label>
                  <input
                    type="text"
                    value={reData.city}
                    onChange={(e) => setReData((p) => ({ ...p, city: e.target.value }))}
                    placeholder={t('addRealEstate.cityPlaceholder' as any)}
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <label className={lblCls}>
                    {t('addRealEstate.country' as any)}{' '}
                    <span className="text-gray-400 text-xs font-normal">
                      {t('addRealEstate.optional' as any)}
                    </span>
                  </label>
                  <input
                    type="text"
                    value={reData.country}
                    onChange={(e) => setReData((p) => ({ ...p, country: e.target.value }))}
                    placeholder={t('addRealEstate.countryPlaceholder' as any)}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Currency */}
              {renderCurrencySelector()}

              <button
                type="button"
                onClick={() => setReTab('characteristics')}
                className="w-full py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-all flex items-center justify-center gap-2"
              >
                {t('addRealEstate.next' as any)}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Tab: Characteristics */}
          {reTab === 'characteristics' && (
            <div className="space-y-5">
              <div className="space-y-2">
                <label className={lblCls}>{t('addRealEstate.purchasePrice' as any)}</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
                    {accountCurrencyConfig.symbol}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={reData.purchasePrice}
                    onChange={(e) =>
                      setReData((p) => ({
                        ...p,
                        purchasePrice: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    placeholder="250 000"
                    className={`${inputCls} pl-11`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={lblCls}>
                    <Ruler className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />
                    {t('addRealEstate.surface' as any)}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reData.surface}
                      onChange={(e) =>
                        setReData((p) => ({
                          ...p,
                          surface: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      placeholder="75"
                      className={`${inputCls} pr-12`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      m²
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className={lblCls}>{t('addRealEstate.landSurface' as any)}</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reData.landSurface}
                      onChange={(e) =>
                        setReData((p) => ({
                          ...p,
                          landSurface: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      placeholder="200"
                      className={`${inputCls} pr-12`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      m²
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={lblCls}>
                    <Calendar className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />
                    {t('addRealEstate.constructionYear' as any)}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={reData.constructionYear}
                    onChange={(e) =>
                      setReData((p) => ({
                        ...p,
                        constructionYear: e.target.value.replace(/[^0-9]/g, '').slice(0, 4),
                      }))
                    }
                    placeholder="2005"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <label className={lblCls}>{t('addRealEstate.dpe' as any)}</label>
                  <CustomSelect
                    value={reData.dpe}
                    onChange={(value) => setReData((p) => ({ ...p, dpe: value }))}
                    placeholder={t('addRealEstate.optional' as any)}
                    options={['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((v) => ({
                      value: v,
                      label: v,
                    }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={lblCls}>
                    {t('addRealEstate.agencyFees' as any)}{' '}
                    <span className="text-gray-400 text-xs font-normal">
                      {t('addRealEstate.optional' as any)}
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
                      {accountCurrencyConfig.symbol}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reData.agencyFees}
                      onChange={(e) =>
                        setReData((p) => ({
                          ...p,
                          agencyFees: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      className={`${inputCls} pl-11`}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className={lblCls}>
                    {t('addRealEstate.notaryFees' as any)}{' '}
                    <span className="text-gray-400 text-xs font-normal">
                      {t('addRealEstate.optional' as any)}
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
                      {accountCurrencyConfig.symbol}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reData.notaryFees}
                      onChange={(e) =>
                        setReData((p) => ({
                          ...p,
                          notaryFees: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      className={`${inputCls} pl-11`}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={lblCls}>
                    {t('addRealEstate.renovationFees' as any)}{' '}
                    <span className="text-gray-400 text-xs font-normal">
                      {t('addRealEstate.optional' as any)}
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
                      {accountCurrencyConfig.symbol}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reData.renovationFees}
                      onChange={(e) =>
                        setReData((p) => ({
                          ...p,
                          renovationFees: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      className={`${inputCls} pl-11`}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className={lblCls}>
                    {t('addRealEstate.furnishingFees' as any)}{' '}
                    <span className="text-gray-400 text-xs font-normal">
                      {t('addRealEstate.optional' as any)}
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500">
                      {accountCurrencyConfig.symbol}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reData.furnishingFees}
                      onChange={(e) =>
                        setReData((p) => ({
                          ...p,
                          furnishingFees: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      className={`${inputCls} pl-11`}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className={lblCls}>
                  <Calendar className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />
                  {t('addRealEstate.purchaseDate' as any)}{' '}
                  <span className="text-gray-400 text-xs font-normal">
                    {t('addRealEstate.optional' as any)}
                  </span>
                </label>
                <DatePicker
                  selected={
                    reData.purchaseDate ? new Date(reData.purchaseDate + 'T00:00:00') : null
                  }
                  onChange={(date) =>
                    setReData((p) => ({
                      ...p,
                      purchaseDate: date ? date.toISOString().split('T')[0] : '',
                    }))
                  }
                  dateFormat="dd/MM/yyyy"
                  className={inputCls}
                  wrapperClassName="w-full"
                  isClearable
                />
              </div>

              <button
                type="button"
                onClick={() => setReTab('ownership')}
                className="w-full py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-all flex items-center justify-center gap-2"
              >
                {t('addRealEstate.next' as any)}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Tab: Ownership */}
          {reTab === 'ownership' && (
            <div className="space-y-5">
              <div className="space-y-2">
                <label className={lblCls}>{t('addRealEstate.ownershipPercent' as any)}</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={reData.ownershipPercent}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.,]/g, '');
                      setReData((p) => ({ ...p, ownershipPercent: val }));
                    }}
                    placeholder="100"
                    className={`${inputCls} pr-11`}
                  />
                  <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {t('addRealEstate.ownershipHint' as any)}
                </p>
              </div>

              {/* Summary */}
              {reData.purchasePrice && (
                <div className="p-4 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/30">
                  <p className="text-sm text-teal-700 dark:text-teal-300 font-medium">
                    {t('addRealEstate.estimatedValue' as any)}
                  </p>
                  <p className="text-2xl font-bold text-teal-700 dark:text-teal-300 mt-1">
                    {accountCurrencyConfig.symbol}{' '}
                    {(
                      (parseFloat(reData.purchasePrice) || 0) *
                      ((parseFloat(reData.ownershipPercent) || 100) / 100)
                    ).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-teal-600/70 dark:text-teal-400/70 mt-1">
                    {reData.ownershipPercent || '100'}% × {accountCurrencyConfig.symbol}{' '}
                    {parseFloat(reData.purchasePrice).toLocaleString('fr-FR', {
                      maximumFractionDigits: 0,
                    })}
                  </p>
                </div>
              )}

              {/* Currency */}
              {renderCurrencySelector()}

              {/* Submit */}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3.5 bg-[#16c47f] hover:bg-[#12b070] disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 mt-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('accounts.modal.saving' as any)}
                  </>
                ) : isEditMode ? (
                  t('addBank.saveChanges' as any)
                ) : (
                  t('addRealEstate.addProperty' as any)
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    );
  };

  // Other asset form
  type OtherAssetTab = 'info' | 'ownership';
  const [oaTab, setOaTab] = useState<OtherAssetTab>('info');
  const [oaData, setOaData] = useState({
    name: '',
    category: '',
    quantity: '1',
    purchaseUnitPrice: '',
    currentUnitPrice: '',
    ownershipPercent: '100',
  });

  const OTHER_ASSET_CATEGORIES = [
    'other',
    'jewelry',
    'pokemon_cards',
    'real_estate_crowdfunding',
    'private_debt',
    'fcpi',
    'g3f',
    'forestry_investment',
    'hedge_fund',
    'watch',
    'sneakers',
    'art',
    'precious_stones',
    'p2p_lending',
    'private_equity',
    'land',
    'wine',
    'car',
    'farm',
    'agriculture',
    'livestock',
    'taxi_license',
  ];

  const handleOtherAssetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oaData.name) return;
    setSaving(true);
    try {
      const qty = parseFloat(oaData.quantity) || 1;
      const currentPrice =
        parseFloat(oaData.currentUnitPrice) || parseFloat(oaData.purchaseUnitPrice) || 0;
      const ownershipPct = parseFloat(oaData.ownershipPercent) || 100;
      const totalValue = qty * currentPrice * (ownershipPct / 100);

      const metadataPayload = {
        asset_category: oaData.category || 'other',
        quantity: qty,
        purchase_unit_price: parseFloat(oaData.purchaseUnitPrice) || null,
        current_unit_price: currentPrice || null,
        ownership_percent: ownershipPct,
      };

      if (isEditMode && editAccountId) {
        await updateAccount(editAccountId, {
          name: oaData.name,
          balance: totalValue,
          metadata: metadataPayload,
        });
        navigate(`/accounts/${editAccountId}`);
      } else {
        await createAccount({
          name: oaData.name,
          type: 'asset',
          subtype: 'other_asset',
          balance: totalValue,
          metadata: metadataPayload,
          currency: nonBankCurrency,
        });
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving other asset:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderOtherAssetForm = () => {
    const qty = parseFloat(oaData.quantity) || 1;
    const currentPrice =
      parseFloat(oaData.currentUnitPrice) || parseFloat(oaData.purchaseUnitPrice) || 0;
    const ownershipPct = parseFloat(oaData.ownershipPercent) || 100;
    const totalValue = qty * currentPrice * (ownershipPct / 100);

    const tabs: { key: OtherAssetTab; label: string }[] = [
      { key: 'info', label: t('addOtherAsset.tabInfo' as any) },
      { key: 'ownership', label: t('addOtherAsset.tabOwnership' as any) },
    ];

    return (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? t('addBank.editAccount' as any) : t('addOtherAsset.title' as any)}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            {t('addOtherAsset.subtitle' as any)}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setOaTab(tab.key)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                oaTab === tab.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleOtherAssetSubmit} className="space-y-5">
          {oaTab === 'info' && (
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addOtherAsset.name' as any)} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={oaData.name}
                  onChange={(e) => setOaData((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all"
                  placeholder={t('addOtherAsset.namePlaceholder' as any)}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addOtherAsset.category' as any)}
                </label>
                <CustomSelect
                  value={oaData.category}
                  onChange={(value) => setOaData((p) => ({ ...p, category: value }))}
                  options={OTHER_ASSET_CATEGORIES.map((cat) => ({
                    value: cat,
                    label: t(`addOtherAsset.categories.${cat}` as any),
                  }))}
                />
              </div>

              {/* Quantity + Purchase Unit Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('addOtherAsset.quantity' as any)}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={oaData.quantity}
                    onChange={(e) =>
                      setOaData((p) => ({
                        ...p,
                        quantity: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('addOtherAsset.purchaseUnitPrice' as any)}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={oaData.purchaseUnitPrice}
                      onChange={(e) =>
                        setOaData((p) => ({
                          ...p,
                          purchaseUnitPrice: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      className="w-full px-4 py-3 pr-14 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all"
                      placeholder="0"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">
                      {accountCurrencyConfig.code}
                    </span>
                  </div>
                </div>
              </div>

              {/* Current Unit Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addOtherAsset.currentUnitPrice' as any)}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={oaData.currentUnitPrice}
                    onChange={(e) =>
                      setOaData((p) => ({
                        ...p,
                        currentUnitPrice: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 pr-14 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all"
                    placeholder={t('addOtherAsset.currentPricePlaceholder' as any)}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">
                    {accountCurrencyConfig.code}
                  </span>
                </div>
              </div>
            </div>
          )}

          {oaTab === 'ownership' && (
            <div className="space-y-5">
              {/* Ownership Percent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addOtherAsset.ownershipPercent' as any)}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={oaData.ownershipPercent}
                    onChange={(e) =>
                      setOaData((p) => ({
                        ...p,
                        ownershipPercent: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 pr-10 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all"
                    placeholder="100"
                  />
                  <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              {/* Total value summary */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-5 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium mb-1">
                  {t('addOtherAsset.estimatedValue' as any)}
                </p>
                <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                  {new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
                    style: 'currency',
                    currency: accountCurrencyConfig.code,
                  }).format(totalValue)}
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  {t('addOtherAsset.estimatedHint' as any)}
                </p>
              </div>
            </div>
          )}

          {/* Currency */}
          {renderCurrencySelector()}

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={saving || !oaData.name}
              className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-gray-300 disabled:to-gray-300 dark:disabled:from-gray-700 dark:disabled:to-gray-700 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/20 transition-all duration-200 transform hover:scale-[1.01] disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isEditMode ? (
                t('addBank.saveChanges' as any)
              ) : (
                t('addOtherAsset.addAsset' as any)
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Watches & Jewelry form
  type WjTab = 'info' | 'ownership';
  const [wjTab, setWjTab] = useState<WjTab>('info');
  const [wjData, setWjData] = useState({
    itemType: 'watch' as 'watch' | 'jewelry',
    name: '',
    purchasePrice: '',
    condition: '',
    hasOriginalBox: false,
    hasPapers: false,
    seller: '',
    ownershipPercent: '100',
  });
  const [wjSuggestions, setWjSuggestions] = useState<
    { brand: string; model: string; reference?: string }[]
  >([]);
  const [wjShowSuggestions, setWjShowSuggestions] = useState(false);

  const handleWjNameChange = (value: string) => {
    setWjData((p) => ({ ...p, name: value }));
    if (wjData.itemType === 'watch') {
      const results = searchWatchModels(value);
      setWjSuggestions(results);
      setWjShowSuggestions(results.length > 0);
    }
  };

  const handleWjSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wjData.name) return;
    setSaving(true);
    try {
      const purchasePrice = parseFloat(wjData.purchasePrice) || 0;
      const ownershipPct = parseFloat(wjData.ownershipPercent) || 100;
      const totalValue = purchasePrice * (ownershipPct / 100);

      const metadataPayload = {
        purchase_price: purchasePrice || null,
        condition: wjData.condition || null,
        has_original_box: wjData.hasOriginalBox,
        has_papers: wjData.hasPapers,
        seller: wjData.seller || null,
        ownership_percent: ownershipPct,
      };

      if (isEditMode && editAccountId) {
        await updateAccount(editAccountId, {
          name: wjData.name,
          balance: totalValue,
          metadata: metadataPayload,
        });
        navigate(`/accounts/${editAccountId}`);
      } else {
        await createAccount({
          name: wjData.name,
          type: 'asset',
          subtype: wjData.itemType,
          balance: totalValue,
          metadata: metadataPayload,
          currency: nonBankCurrency,
        });
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving watch/jewelry:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderWatchesJewelryForm = () => {
    const ownershipPct = parseFloat(wjData.ownershipPercent) || 100;
    const purchasePrice = parseFloat(wjData.purchasePrice) || 0;
    const totalValue = purchasePrice * (ownershipPct / 100);

    const tabs: { key: WjTab; label: string }[] = [
      { key: 'info', label: t('addWatchJewelry.tabInfo' as any) },
      { key: 'ownership', label: t('addWatchJewelry.tabOwnership' as any) },
    ];

    return (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
            <Watch className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? t('addBank.editAccount' as any) : t('addWatchJewelry.title' as any)}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            {t('addWatchJewelry.subtitle' as any)}
          </p>
        </div>

        {/* Type selector */}
        {!isEditMode && (
          <div className="flex gap-3 mb-6">
            <button
              type="button"
              onClick={() => setWjData((p) => ({ ...p, itemType: 'watch', name: '' }))}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                wjData.itemType === 'watch'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                  : 'border-gray-100 dark:border-gray-700/50 text-gray-500 dark:text-gray-400 hover:border-gray-300'
              }`}
            >
              <Watch className="w-4 h-4" />
              {t('addWatchJewelry.typeWatch' as any)}
            </button>
            <button
              type="button"
              onClick={() => setWjData((p) => ({ ...p, itemType: 'jewelry', name: '' }))}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                wjData.itemType === 'jewelry'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                  : 'border-gray-100 dark:border-gray-700/50 text-gray-500 dark:text-gray-400 hover:border-gray-300'
              }`}
            >
              <Gem className="w-4 h-4" />
              {t('addWatchJewelry.typeJewelry' as any)}
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setWjTab(tab.key)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                wjTab === tab.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleWjSubmit} className="space-y-5">
          {wjTab === 'info' && (
            <div className="space-y-5">
              {/* Model name with autocomplete for watches */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {wjData.itemType === 'watch'
                    ? t('addWatchJewelry.model' as any)
                    : t('addWatchJewelry.name' as any)}{' '}
                  <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  {wjData.itemType === 'watch' && (
                    <Watch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  )}
                  <input
                    type="text"
                    value={wjData.name}
                    onChange={(e) => handleWjNameChange(e.target.value)}
                    onFocus={() => wjSuggestions.length > 0 && setWjShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setWjShowSuggestions(false), 200)}
                    className={`w-full ${wjData.itemType === 'watch' ? 'pl-11' : 'px-4'} pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all`}
                    placeholder={
                      wjData.itemType === 'watch'
                        ? t('addWatchJewelry.modelPlaceholder' as any)
                        : t('addWatchJewelry.namePlaceholder' as any)
                    }
                  />
                </div>
                {/* Autocomplete dropdown */}
                {wjShowSuggestions && wjSuggestions.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {wjSuggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onMouseDown={() => {
                          setWjData((p) => ({ ...p, name: `${s.brand} ${s.model}` }));
                          setWjShowSuggestions(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {s.brand} {s.model}
                        </span>
                        {s.reference && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                            Ref. {s.reference}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Purchase price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addWatchJewelry.purchasePrice' as any)}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={wjData.purchasePrice}
                    onChange={(e) =>
                      setWjData((p) => ({
                        ...p,
                        purchasePrice: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 pr-14 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all"
                    placeholder="0"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">
                    {accountCurrencyConfig.code}
                  </span>
                </div>
              </div>

              {/* Seller */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addWatchJewelry.seller' as any)}
                </label>
                <input
                  type="text"
                  value={wjData.seller}
                  onChange={(e) => setWjData((p) => ({ ...p, seller: e.target.value }))}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all"
                  placeholder={t('addWatchJewelry.sellerPlaceholder' as any)}
                />
              </div>

              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addWatchJewelry.condition' as any)}
                </label>
                <CustomSelect
                  value={wjData.condition}
                  onChange={(value) => setWjData((p) => ({ ...p, condition: value }))}
                  placeholder={t('addWatchJewelry.conditionOptional' as any)}
                  options={[
                    {
                      value: 'never_worn',
                      label: t('addWatchJewelry.condition.never_worn' as any),
                    },
                    { value: 'worn', label: t('addWatchJewelry.condition.worn' as any) },
                  ]}
                />
              </div>

              {/* Toggles */}
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('addWatchJewelry.originalBox' as any)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setWjData((p) => ({ ...p, hasOriginalBox: !p.hasOriginalBox }))}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      wjData.hasOriginalBox ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        wjData.hasOriginalBox ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('addWatchJewelry.papers' as any)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setWjData((p) => ({ ...p, hasPapers: !p.hasPapers }))}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      wjData.hasPapers ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        wjData.hasPapers ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {wjTab === 'ownership' && (
            <div className="space-y-5">
              {/* Ownership Percent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addWatchJewelry.ownershipPercent' as any)}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={wjData.ownershipPercent}
                    onChange={(e) =>
                      setWjData((p) => ({
                        ...p,
                        ownershipPercent: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 pr-10 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all"
                    placeholder="100"
                  />
                  <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              {/* Total value */}
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-5 border border-purple-200 dark:border-purple-800">
                <p className="text-sm text-purple-700 dark:text-purple-300 font-medium mb-1">
                  {t('addWatchJewelry.estimatedValue' as any)}
                </p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
                    style: 'currency',
                    currency: accountCurrencyConfig.code,
                  }).format(totalValue)}
                </p>
              </div>
            </div>
          )}

          {/* Currency */}
          {renderCurrencySelector()}

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={saving || !wjData.name}
              className="w-full py-3.5 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 disabled:from-gray-300 disabled:to-gray-300 dark:disabled:from-gray-700 dark:disabled:to-gray-700 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/20 transition-all duration-200 transform hover:scale-[1.01] disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isEditMode ? (
                t('addBank.saveChanges' as any)
              ) : (
                t('addWatchJewelry.addItem' as any)
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Precious Metals form
  type PmStep = 'select' | 'form';
  type PmTab = 'info' | 'ownership';
  const [pmStep, setPmStep] = useState<PmStep>('select');
  const [pmTab, setPmTab] = useState<PmTab>('info');
  const [pmSearch, setPmSearch] = useState('');
  const [pmData, setPmData] = useState({
    name: '',
    metalId: '',
    metalCategory: '' as string,
    quantity: '1',
    purchaseUnitPrice: '',
    ownershipPercent: '100',
  });

  const pmSearchResults = useMemo(() => {
    if (!pmSearch) return [];
    return searchPreciousMetals(pmSearch);
  }, [pmSearch]);

  const pmPopular = useMemo(() => getPopularMetals(), []);

  const handlePmSelectMetal = (metal: PreciousMetal) => {
    const name = language === 'fr' ? metal.name_fr : metal.name_en;
    setPmData((p) => ({ ...p, name, metalId: metal.id, metalCategory: metal.category }));
    setPmStep('form');
    setPmSearch('');
  };

  const handlePmSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pmData.name) return;
    setSaving(true);
    try {
      const qty = parseFloat(pmData.quantity) || 1;
      const unitPrice = parseFloat(pmData.purchaseUnitPrice) || 0;
      const ownershipPct = parseFloat(pmData.ownershipPercent) || 100;
      const totalValue = qty * unitPrice * (ownershipPct / 100);

      const metadataPayload = {
        metal_id: pmData.metalId || null,
        metal_category: pmData.metalCategory || 'other',
        quantity: qty,
        purchase_unit_price: unitPrice || null,
        ownership_percent: ownershipPct,
      };

      if (isEditMode && editAccountId) {
        await updateAccount(editAccountId, {
          name: pmData.name,
          balance: totalValue,
          metadata: metadataPayload,
        });
        navigate(`/accounts/${editAccountId}`);
      } else {
        await createAccount({
          name: pmData.name,
          type: 'asset',
          subtype: 'precious_metal',
          balance: totalValue,
          metadata: metadataPayload,
          currency: nonBankCurrency,
        });
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving precious metal:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderPreciousMetalsForm = () => {
    // Metal selection screen
    if (step === 'precious-metals-form' && pmStep === 'select' && !isEditMode) {
      const displayList = pmSearch ? pmSearchResults : pmPopular;
      return (
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-500/20">
              <Coins className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('addPreciousMetal.selectTitle' as any)}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              {t('addPreciousMetal.selectSubtitle' as any)}
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={pmSearch}
              onChange={(e) => setPmSearch(e.target.value)}
              placeholder={t('addPreciousMetal.searchPlaceholder' as any)}
              className="w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500 transition-all"
              autoFocus
            />
          </div>

          {/* Label */}
          <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 px-1">
            {pmSearch ? t('addPreciousMetal.results' as any) : t('addPreciousMetal.popular' as any)}
          </p>

          {/* Metal list */}
          <div className="space-y-1">
            {displayList.map((metal) => (
              <button
                key={metal.id}
                type="button"
                onClick={() => handlePmSelectMetal(metal)}
                className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left group"
              >
                <span className="text-2xl">{metal.emoji}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors">
                  {language === 'fr' ? metal.name_fr : metal.name_en}
                </span>
              </button>
            ))}
            {pmSearch && displayList.length === 0 && (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
                {t('addPreciousMetal.noResults' as any)}
              </p>
            )}
          </div>

          {/* Custom metal button */}
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700/50">
            <button
              type="button"
              onClick={() => {
                setPmData((p) => ({ ...p, name: '', metalId: 'custom', metalCategory: 'other' }));
                setPmStep('form');
              }}
              className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-yellow-500 hover:text-yellow-600 dark:hover:text-yellow-400 transition-all text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              {t('addPreciousMetal.addCustom' as any)}
            </button>
          </div>
        </div>
      );
    }

    // Form screen (after metal selection or edit mode)
    const qty = parseFloat(pmData.quantity) || 1;
    const unitPrice = parseFloat(pmData.purchaseUnitPrice) || 0;
    const ownershipPct = parseFloat(pmData.ownershipPercent) || 100;
    const totalValue = qty * unitPrice * (ownershipPct / 100);

    const tabs: { key: PmTab; label: string }[] = [
      { key: 'info', label: t('addPreciousMetal.tabInfo' as any) },
      { key: 'ownership', label: t('addPreciousMetal.tabOwnership' as any) },
    ];

    return (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-500/20">
            <Coins className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? t('addBank.editAccount' as any) : t('addPreciousMetal.title' as any)}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            {t('addPreciousMetal.subtitle' as any)}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setPmTab(tab.key)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                pmTab === tab.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handlePmSubmit} className="space-y-5">
          {pmTab === 'info' && (
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addPreciousMetal.name' as any)} <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Coins className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={pmData.name}
                    onChange={(e) => setPmData((p) => ({ ...p, name: e.target.value }))}
                    className="w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500 transition-all"
                    placeholder={t('addPreciousMetal.namePlaceholder' as any)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Quantity + Purchase Unit Price side by side */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('addPreciousMetal.quantity' as any)}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={pmData.quantity}
                    onChange={(e) =>
                      setPmData((p) => ({
                        ...p,
                        quantity: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500 transition-all"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('addPreciousMetal.purchaseUnitPrice' as any)}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={pmData.purchaseUnitPrice}
                      onChange={(e) =>
                        setPmData((p) => ({
                          ...p,
                          purchaseUnitPrice: e.target.value.replace(/[^0-9.,]/g, ''),
                        }))
                      }
                      className="w-full px-4 py-3 pr-14 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500 transition-all"
                      placeholder="0"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">
                      {accountCurrencyConfig.code}
                    </span>
                  </div>
                </div>
              </div>

              {/* Total value preview */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">
                    {t('addPreciousMetal.totalValue' as any)}
                  </p>
                  <p className="text-lg font-bold text-yellow-900 dark:text-yellow-100">
                    {new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
                      style: 'currency',
                      currency: accountCurrencyConfig.code,
                    }).format(qty * unitPrice)}
                  </p>
                </div>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                  {qty} ×{' '}
                  {new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
                    style: 'currency',
                    currency: accountCurrencyConfig.code,
                  }).format(unitPrice)}
                </p>
              </div>
            </div>
          )}

          {pmTab === 'ownership' && (
            <div className="space-y-5">
              {/* Ownership Percent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('addPreciousMetal.ownershipPercent' as any)}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={pmData.ownershipPercent}
                    onChange={(e) =>
                      setPmData((p) => ({
                        ...p,
                        ownershipPercent: e.target.value.replace(/[^0-9.,]/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 pr-10 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500 transition-all"
                    placeholder="100"
                  />
                  <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              {/* Total value */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-5 border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium mb-1">
                  {t('addPreciousMetal.estimatedValue' as any)}
                </p>
                <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                  {new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
                    style: 'currency',
                    currency: accountCurrencyConfig.code,
                  }).format(totalValue)}
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                  {t('addPreciousMetal.estimatedHint' as any)}
                </p>
              </div>
            </div>
          )}

          {/* Currency */}
          {renderCurrencySelector()}

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={saving || !pmData.name}
              className="w-full py-3.5 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 disabled:from-gray-300 disabled:to-gray-300 dark:disabled:from-gray-700 dark:disabled:to-gray-700 text-white font-semibold rounded-xl shadow-lg shadow-yellow-500/20 transition-all duration-200 transform hover:scale-[1.01] disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isEditMode ? (
                t('addBank.saveChanges' as any)
              ) : (
                t('addPreciousMetal.addMetal' as any)
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Crypto form
  type CryptoStep = 'select' | 'form';
  const [cryptoStep, setCryptoStep] = useState<CryptoStep>('select');
  const [cryptoSearch, setCryptoSearch] = useState('');
  const [cryptoSearchResults, setCryptoSearchResults] = useState<CryptoSearchResult[]>([]);
  const [cryptoSearching, setCryptoSearching] = useState(false);
  const [cryptoData, setCryptoData] = useState({
    name: '',
    coinId: '',
    symbol: '',
    thumb: '',
    quantity: '1',
    purchaseUnitPrice: '',
  });
  const [livePrice, setLivePrice] = useState<CryptoPrice | null>(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);

  // Debounced crypto search
  useEffect(() => {
    if (!cryptoSearch || cryptoSearch.length < 1) {
      setCryptoSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setCryptoSearching(true);
      const results = await searchCryptos(cryptoSearch);
      setCryptoSearchResults(results);
      setCryptoSearching(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [cryptoSearch]);

  // Fetch live price when coin is selected
  useEffect(() => {
    if (!cryptoData.coinId) {
      setLivePrice(null);
      return;
    }
    let cancelled = false;
    const fetchPrice = async () => {
      setLivePriceLoading(true);
      const price = await getCryptoPrice(cryptoData.coinId);
      if (!cancelled) {
        setLivePrice(price);
        setLivePriceLoading(false);
      }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [cryptoData.coinId]);

  const handleCryptoSelect = (
    coin: CryptoSearchResult | { id: string; name: string; symbol: string; thumb: string }
  ) => {
    setCryptoData((p) => ({
      ...p,
      name: coin.name,
      coinId: coin.id,
      symbol: coin.symbol,
      thumb: 'thumb' in coin ? coin.thumb : '',
    }));
    setCryptoStep('form');
    setCryptoSearch('');
  };

  const handleCryptoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cryptoData.name) return;
    setSaving(true);
    try {
      const qty = parseFloat(cryptoData.quantity) || 1;
      const unitPrice = parseFloat(cryptoData.purchaseUnitPrice) || 0;
      const totalValue = qty * unitPrice;

      const metadataPayload = {
        coin_id: cryptoData.coinId || null,
        symbol: cryptoData.symbol || null,
        thumb: cryptoData.thumb || null,
        quantity: qty,
        purchase_unit_price: unitPrice || null,
      };

      if (isEditMode && editAccountId) {
        await updateAccount(editAccountId, {
          name: cryptoData.name,
          balance: totalValue,
          metadata: metadataPayload,
        });
        navigate(`/accounts/${editAccountId}`);
      } else {
        await createAccount({
          name: cryptoData.name,
          type: 'asset',
          subtype: 'crypto',
          balance: totalValue,
          metadata: metadataPayload,
          currency: nonBankCurrency,
        });
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving crypto:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderCryptoForm = () => {
    // Crypto selection screen
    if (step === 'crypto-form' && cryptoStep === 'select' && !isEditMode) {
      const displayList = cryptoSearch
        ? cryptoSearchResults
        : POPULAR_CRYPTOS.map((c) => ({
            id: c.id,
            name: c.name,
            symbol: c.symbol,
            thumb: c.thumb,
            large: '',
            market_cap_rank: null,
          }));
      return (
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Bitcoin className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('addCrypto.selectTitle' as any)}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              {t('addCrypto.selectSubtitle' as any)}
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={cryptoSearch}
              onChange={(e) => setCryptoSearch(e.target.value)}
              placeholder={t('addCrypto.searchPlaceholder' as any)}
              className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
              autoFocus
            />
            {cryptoSearching && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-500 animate-spin" />
            )}
          </div>

          {/* Label */}
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 px-1">
            {cryptoSearch ? t('addCrypto.results' as any) : t('addCrypto.popular' as any)}
          </p>

          {/* Results list */}
          <div className="space-y-2">
            {displayList.length === 0 && cryptoSearch && !cryptoSearching && (
              <p className="text-center text-gray-400 py-8">{t('addCrypto.noResults' as any)}</p>
            )}
            {displayList.map((coin) => (
              <button
                key={coin.id}
                onClick={() => handleCryptoSelect(coin)}
                className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 dark:border-gray-700 hover:border-orange-400 dark:hover:border-orange-500 bg-white dark:bg-gray-800 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {coin.thumb ? (
                    <img src={coin.thumb} alt={coin.name} className="w-7 h-7 rounded-full" />
                  ) : (
                    <Bitcoin className="w-5 h-5 text-orange-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">
                    {coin.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{coin.symbol}</p>
                </div>
                {coin.market_cap_rank && (
                  <span className="text-xs font-medium text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                    #{coin.market_cap_rank}
                  </span>
                )}
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-orange-500 transition-colors" />
              </button>
            ))}
          </div>

          {/* Custom crypto */}
          <button
            onClick={() => {
              setCryptoData((p) => ({ ...p, name: '', coinId: '', symbol: '', thumb: '' }));
              setCryptoStep('form');
            }}
            className="w-full mt-4 flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-600 hover:border-orange-400 dark:hover:border-orange-500 transition-all text-gray-500 hover:text-orange-600 dark:hover:text-orange-400"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">{t('addCrypto.addCustom' as any)}</span>
          </button>
        </div>
      );
    }

    // Crypto form
    const qty = parseFloat(cryptoData.quantity) || 0;
    const unitPrice = parseFloat(cryptoData.purchaseUnitPrice) || 0;
    const totalPurchaseValue = qty * unitPrice;
    const currentPrice = livePrice
      ? accountCurrencyConfig.code === 'EUR'
        ? livePrice.eur
        : livePrice.usd
      : null;
    const currentTotalValue = currentPrice ? qty * currentPrice : null;
    const change24h = livePrice
      ? accountCurrencyConfig.code === 'EUR'
        ? livePrice.eur_24h_change
        : livePrice.usd_24h_change
      : null;
    const pnl = currentTotalValue !== null ? currentTotalValue - totalPurchaseValue : null;
    const pnlPercent =
      totalPurchaseValue > 0 && pnl !== null ? (pnl / totalPurchaseValue) * 100 : null;

    return (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg overflow-hidden">
            {cryptoData.thumb ? (
              <img
                src={cryptoData.thumb}
                alt={cryptoData.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <Bitcoin className="w-8 h-8 text-white" />
            )}
          </div>
          {cryptoData.name && (
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{cryptoData.name}</h3>
          )}
          {cryptoData.symbol && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{cryptoData.symbol}</p>
          )}
        </div>

        {/* Live price card */}
        {cryptoData.coinId && (
          <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border border-orange-200 dark:border-orange-800/50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide">
                  {t('addCrypto.livePrice' as any)}
                </p>
                {livePriceLoading && !livePrice ? (
                  <Loader2 className="w-5 h-5 text-orange-500 animate-spin mt-1" />
                ) : livePrice ? (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                    {currentPrice?.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                      style: 'currency',
                      currency: accountCurrencyConfig.code,
                    })}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 mt-1">—</p>
                )}
              </div>
              {change24h !== null && (
                <div
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold ${
                    change24h >= 0
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  <span>{change24h >= 0 ? '▲' : '▼'}</span>
                  <span>{Math.abs(change24h).toFixed(2)}%</span>
                </div>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleCryptoSubmit} className="space-y-5">
          {/* Name */}
          {!cryptoData.coinId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('addCrypto.name' as any)}
              </label>
              <input
                type="text"
                value={cryptoData.name}
                onChange={(e) => setCryptoData((p) => ({ ...p, name: e.target.value }))}
                placeholder={t('addCrypto.namePlaceholder' as any)}
                className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                required
              />
            </div>
          )}

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('addCrypto.quantity' as any)}
            </label>
            <input
              type="number"
              step="any"
              min="0"
              value={cryptoData.quantity}
              onChange={(e) => setCryptoData((p) => ({ ...p, quantity: e.target.value }))}
              placeholder="1"
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
              required
            />
          </div>

          {/* Purchase unit price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('addCrypto.purchasePrice' as any)}
            </label>
            <div className="relative">
              <input
                type="number"
                step="any"
                min="0"
                value={cryptoData.purchaseUnitPrice}
                onChange={(e) =>
                  setCryptoData((p) => ({ ...p, purchaseUnitPrice: e.target.value }))
                }
                placeholder="0.00"
                className="w-full px-4 py-3 pr-14 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                required
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">
                {accountCurrencyConfig.code}
              </span>
            </div>
          </div>

          {/* P&L Preview */}
          {qty > 0 && unitPrice > 0 && (
            <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 space-y-3">
              {/* Purchase total */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('addCrypto.totalPurchase' as any)}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {totalPurchaseValue.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    style: 'currency',
                    currency: accountCurrencyConfig.code,
                  })}
                </span>
              </div>
              {/* Current value */}
              {currentTotalValue !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('addCrypto.currentValue' as any)}
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {currentTotalValue.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                      style: 'currency',
                      currency: accountCurrencyConfig.code,
                    })}
                  </span>
                </div>
              )}
              {/* P&L */}
              {pnl !== null && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('addCrypto.pnl' as any)}
                  </span>
                  <div className="text-right">
                    <span
                      className={`text-sm font-bold ${pnl >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                    >
                      {pnl >= 0 ? '+' : ''}
                      {pnl.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                        style: 'currency',
                        currency: accountCurrencyConfig.code,
                      })}
                    </span>
                    {pnlPercent !== null && (
                      <span
                        className={`ml-2 text-xs font-semibold ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}
                      >
                        ({pnl >= 0 ? '+' : ''}
                        {pnlPercent.toFixed(2)}%)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Currency */}
          {renderCurrencySelector()}

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving || !cryptoData.name}
              className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-2xl shadow-lg shadow-orange-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t(isEditMode ? ('common.save' as any) : ('addCrypto.addCrypto' as any))
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  // PEA (Plan d'Épargne en Actions) form
  type PeaStep = 'select' | 'form';
  const [peaStep, setPeaStep] = useState<PeaStep>('select');
  const [peaSearch, setPeaSearch] = useState('');
  const [peaData, setPeaData] = useState({
    name: '',
    stockId: '',
    symbol: '',
    stockType: 'stock' as string,
    quantity: '1',
    purchaseUnitPrice: '',
  });

  const peaDisplayList = peaSearch ? searchStocks(peaSearch) : getPopularStocks();

  const handlePeaSelect = (stock: Stock) => {
    setPeaData((p) => ({
      ...p,
      name: stock.name,
      stockId: stock.id,
      symbol: stock.symbol,
      stockType: stock.type,
    }));
    setPeaStep('form');
    setPeaSearch('');
  };

  const handlePeaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!peaData.name) return;
    setSaving(true);
    try {
      const qty = parseFloat(peaData.quantity) || 1;
      const unitPrice = parseFloat(peaData.purchaseUnitPrice) || 0;
      const totalValue = qty * unitPrice;

      const metadataPayload = {
        stock_id: peaData.stockId || null,
        symbol: peaData.symbol || null,
        stock_type: peaData.stockType || 'stock',
        quantity: qty,
        purchase_unit_price: unitPrice || null,
      };

      if (isEditMode && editAccountId) {
        await updateAccount(editAccountId, {
          name: peaData.name,
          balance: totalValue,
          metadata: metadataPayload,
        });
        navigate(`/accounts/${editAccountId}`);
      } else {
        await createAccount({
          name: peaData.name,
          type: 'asset',
          subtype: 'pea_stock' as any,
          balance: totalValue,
          metadata: metadataPayload,
          currency: nonBankCurrency,
        });
        navigate('/accounts');
      }
    } catch (err) {
      console.error('Error saving PEA stock:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderPeaForm = () => {
    // PEA selection screen
    if (step === 'pea-form' && peaStep === 'select' && !isEditMode) {
      return (
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('addPea.selectTitle' as any)}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              {t('addPea.selectSubtitle' as any)}
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={peaSearch}
              onChange={(e) => setPeaSearch(e.target.value)}
              placeholder={t('addPea.searchPlaceholder' as any)}
              className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
              autoFocus
            />
          </div>

          {/* Label */}
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 px-1">
            {peaSearch ? t('addPea.results' as any) : t('addPea.popular' as any)}
          </p>

          {/* Results list */}
          <div className="space-y-2">
            {peaDisplayList.length === 0 && peaSearch && (
              <p className="text-center text-gray-400 py-8">{t('addPea.noResults' as any)}</p>
            )}
            {peaDisplayList.map((stock) => (
              <button
                key={stock.id}
                onClick={() => handlePeaSelect(stock)}
                className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-800 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center flex-shrink-0">
                  {stock.type === 'etf' ? (
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  ) : (
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">
                    {stock.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {stock.symbol} · {stock.exchange}
                  </p>
                </div>
                <span className="text-xs font-medium text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full uppercase">
                  {stock.type}
                </span>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
              </button>
            ))}
          </div>

          {/* Custom stock */}
          <button
            onClick={() => {
              setPeaData((p) => ({ ...p, name: '', stockId: '', symbol: '', stockType: 'stock' }));
              setPeaStep('form');
            }}
            className="w-full mt-4 flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 transition-all text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">{t('addPea.addCustom' as any)}</span>
          </button>
        </div>
      );
    }

    // PEA form
    const qty = parseFloat(peaData.quantity) || 0;
    const unitPrice = parseFloat(peaData.purchaseUnitPrice) || 0;
    const totalValue = qty * unitPrice;

    return (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            {peaData.stockType === 'etf' ? (
              <TrendingUp className="w-8 h-8 text-white" />
            ) : (
              <BarChart3 className="w-8 h-8 text-white" />
            )}
          </div>
          {peaData.name && (
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{peaData.name}</h3>
          )}
          {peaData.symbol && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{peaData.symbol}</p>
          )}
        </div>

        <form onSubmit={handlePeaSubmit} className="space-y-5">
          {/* Name (only if custom) */}
          {!peaData.stockId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('addPea.name' as any)}
              </label>
              <input
                type="text"
                value={peaData.name}
                onChange={(e) => setPeaData((p) => ({ ...p, name: e.target.value }))}
                placeholder={t('addPea.namePlaceholder' as any)}
                className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                required
              />
            </div>
          )}

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('addPea.quantity' as any)}
            </label>
            <input
              type="number"
              step="any"
              min="0"
              value={peaData.quantity}
              onChange={(e) => setPeaData((p) => ({ ...p, quantity: e.target.value }))}
              placeholder="1"
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
              required
            />
          </div>

          {/* Purchase unit price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('addPea.purchasePrice' as any)}
            </label>
            <div className="relative">
              <input
                type="number"
                step="any"
                min="0"
                value={peaData.purchaseUnitPrice}
                onChange={(e) => setPeaData((p) => ({ ...p, purchaseUnitPrice: e.target.value }))}
                placeholder="0.00"
                className="w-full px-4 py-3 pr-14 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                required
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">
                EUR
              </span>
            </div>
          </div>

          {/* Total value preview */}
          {qty > 0 && unitPrice > 0 && (
            <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                  {t('addPea.totalValue' as any)}
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {totalValue.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    style: 'currency',
                    currency: 'EUR',
                  })}
                </span>
              </div>
            </div>
          )}

          {/* Currency */}
          {renderCurrencySelector()}

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving || !peaData.name}
              className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t(isEditMode ? ('common.save' as any) : ('addPea.addStock' as any))
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  const getStepTitle = () => {
    if (isEditMode) return t('addBank.editAccount' as any);
    switch (step) {
      case 'category':
        return t('addBank.categoryTitle' as any);
      case 'method':
        return t('addBank.title' as any);
      case 'bank-select':
        return t('addBank.selectBank' as any);
      case 'account-form':
        return t('addBank.accountDetails' as any);
      case 'cash-form':
        return t('addCash.title' as any);
      case 'real-estate-form':
        return t('addRealEstate.title' as any);
      case 'other-asset-form':
        return t('addOtherAsset.title' as any);
      case 'watches-jewelry-form':
        return t('addWatchJewelry.title' as any);
      case 'precious-metals-form':
        return t('addPreciousMetal.title' as any);
      case 'crypto-form':
        return t('addCrypto.title' as any);
      case 'pea-form':
        return t('addPea.title' as any);
    }
  };

  const handleBack = () => {
    if (isEditMode) {
      navigate(editAccountId ? `/accounts/${editAccountId}` : '/accounts');
      return;
    }
    switch (step) {
      case 'category':
        navigate('/accounts');
        break;
      case 'method':
        setStep('category');
        break;
      case 'bank-select':
        setStep('method');
        setSearchQuery('');
        break;
      case 'account-form':
        setStep('bank-select');
        setSelectedBank(null);
        break;
      case 'cash-form':
        setStep('category');
        break;
      case 'real-estate-form':
        setStep('category');
        break;
      case 'other-asset-form':
        setStep('category');
        break;
      case 'watches-jewelry-form':
        setStep('category');
        break;
      case 'precious-metals-form':
        if (pmStep === 'form' && !isEditMode) {
          setPmStep('select');
          setPmTab('info');
        } else {
          setStep('category');
        }
        break;
      case 'crypto-form':
        if (cryptoStep === 'form' && !isEditMode) {
          setCryptoStep('select');
        } else {
          setStep('category');
        }
        break;
      case 'pea-form':
        if (peaStep === 'form' && !isEditMode) {
          setPeaStep('select');
        } else {
          setStep('category');
        }
        break;
    }
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="accounts" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        {/* Header */}
        <div className="sticky top-0 z-40 bg-[#f9fafb]/80 dark:bg-[#0f1117]/80 backdrop-blur-lg border-b border-gray-100 dark:border-gray-700/50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {getStepTitle()}
              </h1>
              {/* Step indicator */}
              {!isEditMode && (
                <div className="flex items-center gap-1.5 mt-1">
                  {(step === 'cash-form'
                    ? ['category', 'cash-form']
                    : step === 'real-estate-form'
                      ? ['category', 'real-estate-form']
                      : step === 'other-asset-form'
                        ? ['category', 'other-asset-form']
                        : step === 'watches-jewelry-form'
                          ? ['category', 'watches-jewelry-form']
                          : step === 'precious-metals-form'
                            ? ['category', 'precious-metals-form']
                            : step === 'crypto-form'
                              ? ['category', 'crypto-form']
                              : step === 'pea-form'
                                ? ['category', 'pea-form']
                                : ['category', 'method', 'bank-select', 'account-form']
                  ).map((s, i, arr) => (
                    <div
                      key={s}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        i <= arr.indexOf(step)
                          ? 'bg-[#16c47f] w-8'
                          : 'bg-gray-200 dark:bg-gray-700 w-4'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          {step === 'category' && renderCategoryStep()}
          {step === 'method' && renderMethodStep()}
          {step === 'bank-select' && renderBankSelectStep()}
          {step === 'account-form' && renderAccountForm()}
          {step === 'cash-form' && renderCashForm()}
          {step === 'real-estate-form' && renderRealEstateForm()}
          {step === 'other-asset-form' && renderOtherAssetForm()}
          {step === 'watches-jewelry-form' && renderWatchesJewelryForm()}
          {step === 'precious-metals-form' && renderPreciousMetalsForm()}
          {step === 'crypto-form' && renderCryptoForm()}
          {step === 'pea-form' && renderPeaForm()}
        </div>
      </main>
    </div>
  );
}
